import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './darkTheme.css'
import './transparencySupport.css'
import App from './App.tsx'
import { useMemoryTokens } from './lib/utils/tokenStorage'

// ── Embedded portal preview (CR #127) ───────────────────────────────────────
// An admin previewing a user's portal opens it in an in-app window, which
// renders THIS app in an iframe at /portal/<role>#at=…&rt=…&embed=1. Seed the
// realm-local in-memory token store before React mounts so AuthContext boots as
// that user — the parent tab's own admin session is never touched.
if (window.self !== window.top && window.location.hash.includes('embed=1')) {
  const p = new URLSearchParams(window.location.hash.slice(1));
  const at = p.get('at'); const rt = p.get('rt');
  if (at && rt) {
    useMemoryTokens(at, rt);
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

// Polyfill for Firefox adoptedStyleSheets compatibility
if (!Array.isArray(document.adoptedStyleSheets)) {
  Object.defineProperty(document, 'adoptedStyleSheets', {
    get() {
      return [];
    },
    set(_sheets: CSSStyleSheet[]) {
      // Firefox doesn't support this, so we'll just ignore it
      console.warn('adoptedStyleSheets not fully supported in this browser');
    }
  });
}

// Ensure document.adoptedStyleSheets has array methods
if (document.adoptedStyleSheets && typeof document.adoptedStyleSheets.filter !== 'function') {
  const originalSheets = document.adoptedStyleSheets;
  const sheetsArray = Array.isArray(originalSheets) ? originalSheets : [];
  Object.defineProperty(document, 'adoptedStyleSheets', {
    get() {
      return sheetsArray;
    },
    set(sheets: CSSStyleSheet[]) {
      sheetsArray.length = 0;
      sheetsArray.push(...(Array.isArray(sheets) ? sheets : []));
    }
  });
}

// Offline support: app-shell + portal-data caching, and replay of submits
// queued while out of signal (see src/lib/offlineQueue.ts)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
